import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import type { MentionContextIdentifier, MentionsResult } from '@atlaskit/mention';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ProjectMentionProvider } from '../project-mention-provider/index.tsx';
import { TeamMentionProvider } from '../team-mention-provider/index.tsx';

const MENTION_DEBOUNCE_MS = 150;

export class UserTeamProjectMentionProvider extends ProjectMentionProvider {
	teamMentionProvider: TeamMentionProvider;

	constructor(
		projectOptions: { projectKey: ProjectKey; projectType?: ProjectType | null },
		teamOptions: { cloudId: string; userId: string | null; orgId?: string },
	) {
		super('', projectOptions.projectKey, projectOptions.projectType);

		this.teamMentionProvider = new TeamMentionProvider({
			cloudId: teamOptions.cloudId,
			userId: teamOptions.userId,
			orgId: teamOptions.orgId,
		});
	}

	/**
	 * We override the filter function to search and interpolate the results from both the project and team mention providers.
	 * This is so that we can only show users that have project access.
	 */
	async filter(query?: string | null, contextIdentifier?: MentionContextIdentifier) {
		try {
			const currentSearchTime = Date.now();

			const mentionResults = await this.debouncedSearch(query, contextIdentifier);
			const results = mentionResults ?? { mentions: [], query: query ?? '' };
			this._notifyListeners(results, {
				duration: Date.now() - currentSearchTime,
			});
			this._notifyAllResultsListeners(results);
		} catch (e) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			this._notifyErrorListeners(e as Error);
		}
	}

	debouncedSearch = debounce(
		async (
			query?: string | null,
			contextIdentifier?: MentionContextIdentifier,
		): Promise<MentionsResult> => {
			const currentSearchTime = Date.now();
			try {
				const teamResults = this.teamMentionProvider.getRecommendedMentions(
					// @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
					query,
					contextIdentifier,
				);
				// @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
				const userResults = this.search(query, contextIdentifier);

				const mentionsQuery = fg('gryf-5309-issue-create-mention-order')
					? [userResults, teamResults]
					: [teamResults, userResults];

				const mentionResults = await Promise.all(mentionsQuery);

				// @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
				this.activeSearches.delete(query);
				// discard the response from previous requests
				if (currentSearchTime > this.lastSearchTime) {
					this.lastSearchTime = currentSearchTime;
				}

				return mentionResults.reduce<MentionsResult>(
					(acc, mentionResult) => {
						if (mentionResult) {
							acc.mentions = [...acc.mentions, ...mentionResult.mentions];
						}
						return acc;
					},
					{ mentions: [], query: query ?? '' },
				);
			} catch (e) {
				if (!isNil(query) && query.length > 0) {
					this.activeSearches.delete(query);
				}

				throw e;
			}
		},
		MENTION_DEBOUNCE_MS,
	);
}
